/**
 * CCO › Segurança: eventos do Cybersecurity Agent e revogação de dispositivos (US-409 · RF-06) — Nexora Grid.
 *
 * Criado em: 17/10/2026 · Última modificação: 25/10/2026 · Versão: 1.2.0
 *
 * O agente observa o broker (conexões, acessos negados pela ACL), as recusas de TLS e as mensagens recusadas pela
 * ingestão. Eventos CRÍTICOS também entram em CCO › Alarmes. Aqui: contagem por severidade, lista com filtro e os
 * dispositivos da operadora — engenharia, supervisor e coordenador (ou o suporte) revogam e reativam.
 *
 * US-3105: eventos de origem "plataforma" (bloqueio por rajada de falhas no backend).
 * US-3107: "Auditoria do agente" (`ver_auditoria_agente`) — quem perguntou o quê, por onde (tela, Telegram, WhatsApp,
 * voz), o que o agente consultou (ferramentas e parâmetros), o que respondeu, confiança e custo.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ErroApi } from "../api";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";
import { useCco } from "./contexto";

type EventoSeg = { id: number; instante: string; origem: string; tipo: string; severidade: string; empresa: string | null; device_id: string | null;
  client_id: string | null; peername: string | null; topico: string | null; motivo: string; alarme_id: string | null };
type DispositivoSeg = { device_id: string; unidade: string; tipo: string; ativo: boolean; revogado_em: string | null; revogado_por: string | null; motivo: string | null };

const SEVERIDADES = ["CRITICO", "ALTO", "ALERTA", "INFO"] as const;
const NOME_SEV: Record<string, string> = { CRITICO: "crítico", ALTO: "alto", ALERTA: "alerta", INFO: "informativo" };
const CLASSE_SEV: Record<string, string> = { CRITICO: "prioridade-1", ALTO: "prioridade-2", ALERTA: "prioridade-3", INFO: "prioridade-4" };
const ORIGEM: Record<string, string> = { broker: "broker", tls: "TLS", ingestao: "ingestão", agente: "agente", backend: "plataforma" };
type Interacao = { id: number; instante: string; login: string; canal: string; conta: string | null; aparelho_id: string | null; origem_texto: string;
  servico: string; pergunta: string; ferramentas: { ferramenta: string; parametros: Record<string, unknown>; sucesso: boolean }[]; fontes: string[];
  resposta: string; confianca: string | null; custo_usd: number; modelo: string; motivo_parada: string };
const CANAL: Record<string, string> = { tela: "tela", telegram: "Telegram", whatsapp: "WhatsApp" };

/** US-3107: interações do agente de IA na operadora (auditoria em banco). */
function AuditoriaAgente({ empresa }: { empresa: string }) {
  const [dias, setDias] = useState(7);
  const [linhas, setLinhas] = useState<Interacao[] | null>(null);
  const [aberta, setAberta] = useState<number | null>(null);
  useEffect(() => {
    api<Interacao[]>(`/v1/auditoria-agente?empresa=${empresa}&dias=${dias}&limite=200`).then(setLinhas).catch(() => setLinhas([]));
  }, [empresa, dias]);
  return (
    <div className="nx-cartao">
      <h3>Auditoria do agente
        <span className="seletor-periodo">{[1, 7, 30].map((d) => (
          <button key={d} type="button" className={`nx-botao${d === dias ? " ativo" : ""}`} onClick={() => setDias(d)}>{d === 1 ? "hoje" : `${d} dias`}</button>))}</span></h3>
      <p className="nx-suave">Cada resposta do agente de IA: quem perguntou, por onde, o que ele consultou (ferramentas e parâmetros) e o que respondeu. Só leitura; nada é apagado.</p>
      <table className="tabela-composicao" aria-label="Auditoria do agente">
        <thead><tr><th>Quando</th><th>Quem / por onde</th><th>Pergunta</th><th>Ferramentas</th><th>Confiança · custo</th></tr></thead>
        <tbody>
          {(linhas ?? []).map((l) => (
            <tr key={l.id} onClick={() => setAberta(aberta === l.id ? null : l.id)} style={{ cursor: "pointer" }}>
              <td>{new Date(l.instante).toLocaleString("pt-BR")}</td>
              <td>{l.login} <small className="nx-suave">{CANAL[l.canal] ?? l.canal}{l.origem_texto === "voz" ? " (voz)" : ""}{l.aparelho_id ? " · 🔐" : ""}</small></td>
              <td>{aberta === l.id ? <>{l.pergunta}<br /><small className="nx-suave">→ {l.resposta}</small></> : l.pergunta.slice(0, 80)}</td>
              <td>{l.ferramentas.length ? l.ferramentas.map((f, i) => <span key={i} className={f.sucesso ? "" : "erro-painel"}
                title={JSON.stringify(f.parametros)}>{f.ferramenta}{i < l.ferramentas.length - 1 ? ", " : ""}</span>) : <span className="nx-suave">—</span>}</td>
              <td>{l.confianca ?? "—"} · US$ {l.custo_usd.toFixed(4)}</td>
            </tr>
          ))}
          {linhas && !linhas.length && <tr><td colSpan={5} className="nx-suave">nenhuma interação com o agente no período</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function Seguranca() {
  const { empresa } = useCco();
  const { usuario } = useSessao();
  const [horas, setHoras] = useState(24);
  const [filtro, setFiltro] = useState<string | null>(null);
  const [dados, setDados] = useState<{ eventos: EventoSeg[]; contagem: Record<string, number> } | null>(null);
  const [dispositivos, setDispositivos] = useState<{ dispositivos: DispositivoSeg[]; pode_revogar: boolean } | null>(null);
  const [motivo, setMotivo] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<string | null>(null);

  const carregar = useCallback(() => {
    if (!empresa) return;
    api<{ eventos: EventoSeg[]; contagem: Record<string, number> }>(`/v1/seguranca/eventos?empresa=${empresa}&horas=${horas}${filtro ? `&severidade=${filtro}` : ""}`)
      .then(setDados).catch(() => setDados({ eventos: [], contagem: {} }));
    api<{ dispositivos: DispositivoSeg[]; pode_revogar: boolean }>(`/v1/seguranca/dispositivos?empresa=${empresa}`).then(setDispositivos).catch(() => setDispositivos(null));
  }, [empresa, horas, filtro]);
  useEffect(() => { carregar(); const r = window.setInterval(carregar, 30000); return () => window.clearInterval(r); }, [carregar]);

  const acao = async (d: DispositivoSeg, tipo: "revogar" | "reativar") => {
    try {
      await api(`/v1/seguranca/dispositivos/${d.device_id}/${tipo}`, tipo === "revogar" ? { corpo: { motivo: motivo[d.device_id] ?? "" } } : { metodo: "POST" });
      setAviso(tipo === "revogar" ? `${d.device_id} revogado: a ingestão recusa e o agente desconecta do broker.` : `${d.device_id} reativado.`);
      carregar();
    } catch (e) { setAviso(`Não foi possível: ${e instanceof ErroApi ? e.message : "falha de rede"}`); }
  };

  return (
    <section className="seguranca" aria-label="Segurança">
      <div className="nx-cartao">
        <h3>Segurança · últimas {horas} h
          <span className="seletor-periodo">{[24, 168, 720].map((h) => (
            <button key={h} type="button" className={`nx-botao${h === horas ? " ativo" : ""}`} onClick={() => setHoras(h)}>{h === 24 ? "24 h" : h === 168 ? "7 dias" : "30 dias"}</button>))}</span></h3>
        <div className="contadores-seguranca" role="group" aria-label="Filtro por severidade">
          {SEVERIDADES.map((s) => (
            <button key={s} type="button" className={`nx-cartao contador-seg ${CLASSE_SEV[s]}${filtro === s ? " ativo" : ""}`} aria-pressed={filtro === s}
              onClick={() => setFiltro(filtro === s ? null : s)}>
              <span className="nx-kpi">{dados?.contagem[s] ?? "—"}</span><small>{NOME_SEV[s]}</small>
            </button>
          ))}
        </div>
        <p className="nx-suave">O agente registra conexões de dispositivos desconhecidos ou revogados, acessos negados pela ACL do broker, recusas de TLS (sem certificado, CA desconhecida, expirado) e mensagens recusadas pela ingestão. Eventos críticos e rajadas também viram alarme em CCO › Alarmes.</p>
        <table className="tabela-composicao tabela-seguranca">
          <thead><tr><th>Quando</th><th>Severidade</th><th>Evento</th><th>Origem</th><th>Quem</th><th>Detalhe</th></tr></thead>
          <tbody>
            {(dados?.eventos ?? []).map((e) => (
              <tr key={e.id} className={CLASSE_SEV[e.severidade]}>
                <td className="nx-valor">{new Date(e.instante).toLocaleString("pt-BR")}</td>
                <td><span className="forma">{e.severidade === "CRITICO" ? "◆" : e.severidade === "ALTO" ? "▲" : e.severidade === "ALERTA" ? "●" : "■"}</span> {NOME_SEV[e.severidade]}</td>
                <td>{e.tipo.replace(/_/g, " ").toLowerCase()}{e.alarme_id && <small className="nx-suave"> · alarme no CCO</small>}</td>
                <td>{ORIGEM[e.origem] ?? e.origem}{!e.empresa && <small className="nx-suave"> · plataforma</small>}</td>
                <td>{e.device_id ?? e.client_id ?? "—"}{e.peername && <small className="nx-suave"> · {e.peername}</small>}</td>
                <td className="detalhe-seg">{e.motivo}{e.topico && <small className="nx-suave"> · {e.topico}</small>}</td>
              </tr>
            ))}
            {dados && !dados.eventos.length && <tr><td colSpan={6} className="nx-suave">Nenhum evento no período.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="nx-cartao">
        <h3>Dispositivos da operadora</h3>
        <p className="nx-suave">Revogar: o dispositivo fica inativo (a ingestão recusa tudo o que ele mandar) e o agente o desconecta do broker; se ele voltar a conectar, vira alarme crítico. Use quando um certificado for extraviado ou o equipamento for retirado.</p>
        {aviso && <p className="aviso-paineis" role="status">{aviso}</p>}
        <table className="tabela-composicao">
          <thead><tr><th>Dispositivo</th><th>Unidade</th><th>Situação</th><th /></tr></thead>
          <tbody>
            {(dispositivos?.dispositivos ?? []).map((d) => (
              <tr key={d.device_id}>
                <td><Link to={`/admin/dispositivos/${d.device_id}`} title="Abrir o detalhe do dispositivo">{d.device_id}</Link> <small className="nx-suave">{d.tipo}</small></td>
                <td>{d.unidade}</td>
                <td>{d.revogado_em ? <span className="erro-painel">revogado por {d.revogado_por} em {new Date(d.revogado_em).toLocaleString("pt-BR")} — {d.motivo}</span>
                  : d.ativo ? "ativo" : <span className="nx-suave">inativo</span>}</td>
                <td>{dispositivos?.pode_revogar && (d.revogado_em
                  ? <button type="button" className="nx-botao" onClick={() => void acao(d, "reativar")}>reativar</button>
                  : <span className="decisao-painel">
                      <input value={motivo[d.device_id] ?? ""} onChange={(e) => setMotivo({ ...motivo, [d.device_id]: e.target.value })} maxLength={300}
                        placeholder="motivo da revogação" aria-label={`Motivo da revogação de ${d.device_id}`} />
                      <button type="button" className="nx-botao" disabled={(motivo[d.device_id] ?? "").trim().length < 5} onClick={() => void acao(d, "revogar")}>revogar</button>
                    </span>)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {usuario && empresa && pode.verAuditoriaAgente(usuario) && <AuditoriaAgente empresa={empresa} />}
    </section>
  );
}
